import React, { useCallback, useContext } from 'react';
import { Layout, TextButton, Popover, IconButton, Icon } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import {
  AdditionalDealerData,
  AdditionalDealerDataActionsBottom,
  AdditionalDealerDataContent,
  AdditionalDealerDataDisconnectButton,
  AdditionalDealerDataInfoButton,
  StyledHeadline,
  StyledText,
} from './AudiHeaderAdditionalDealerDataStyles';
import { DealerDetail } from './DealerDetail';
import { DepartmentOpeningHours } from './DepartmentOpeningHours';
import type {
  AudiHeaderDealerDataProps,
  Department,
} from '../../interfaces/header-components.interfaces';
import { upperCase } from '../../utils/string-utils';
import { messages } from '../../i18n/messages';
import { ViewportProvider } from '../../context/Viewport';
import { transformServices } from '../../utils/transformServices';
import { DealerContext } from '../../context';
import useToggle from '../../hooks/use-toggle';
import { NAVIGATION_CLASSNAME } from '../../utils/globals';
import { onDealerTrackingData } from '../../utils/tracking';
import { ServiceContext } from '../../context/ServiceContext';

const AudiHeaderAdditionalDealerData: React.FC<AudiHeaderDealerDataProps> = ({
  open,
  isMobileView,
  refDealerData,
  showDealerSearch,
}) => {
  const { dealerData, removeDealer } = useContext(DealerContext);
  const { trackingService } = useContext(ServiceContext);
  const backToDealerText = useI18n(messages.back_to_dealer);
  const vistDealerText = useI18n(messages.visit_dealer);
  const removeDealerButtonText = useI18n(messages.remove_dealer);
  const removeDealerHelpText = useI18n(messages.remove_dealer_help);
  const [toggleElement, toggle] = useToggle();
  const DealerButtonLabel = showDealerSearch ? vistDealerText : backToDealerText;

  /* NOTE: the default English values for 'partnerServiceLabels' from i18n messages.ts have been purposely removed
  as to not cause confusion in non English markets */
  const partnerServiceLabels = new Map<string, string>([
    ['sales', useI18n(messages.sales)],
    ['sales-r8', useI18n(messages.sales_r8)],
    ['service-r8', useI18n(messages.service_r8)],
    ['service', useI18n(messages.service)],
    ['service-noagent', useI18n(messages.service_no_agent)],
    ['etron', useI18n(messages.etron)],
    ['usedcars', useI18n(messages.usedcars)],
    ['aftersales', useI18n(messages.aftersales)],
    ['accident-specialist', useI18n(messages.accident_specialist)],
    ['atyourdoor', useI18n(messages.atyourdoor)],
    ['silver-car-by-audi', useI18n(messages.silver_car_by_audi)],
    ['ovt', useI18n(messages.ovt)],
    ['audi-on-demand', useI18n(messages.audi_on_demand)],
  ]);
  const modifiedServices =
    dealerData !== undefined && dealerData?.services?.length > 0
      ? transformServices(dealerData.services, partnerServiceLabels)
      : '';

  const dealerHandleClick = useCallback(
    (event: React.MouseEvent, label: string): void => {
      trackingService?.track(onDealerTrackingData(event.target as Element, label));
    },
    [trackingService],
  );

  return (
    <ViewportProvider>
      <AdditionalDealerData
        open={open}
        isMobileView={isMobileView}
        ref={refDealerData}
        role="tabpanel"
        aria-hidden={!open}
        className="dealer-container"
      >
        <AdditionalDealerDataContent isMobileView={isMobileView}>
          {!isMobileView && (
            <Layout className="services">
              <StyledHeadline
                tag="h2"
                variant={{
                  order: '2',
                  type: 'headline',
                  weight: 'normal',
                }}
              >
                {dealerData?.additionalData?.displayName || dealerData?.name || 'Name not found'}
              </StyledHeadline>
              <StyledText tag="p" className="modified-services">
                {upperCase(modifiedServices)}
              </StyledText>
            </Layout>
          )}
          <TextButton
            id="dealerBack"
            variant="secondary"
            size="md"
            href={dealerData?.url || ''}
            onClick={(e: React.MouseEvent) => {
              dealerHandleClick(e, DealerButtonLabel);
            }}
            aria-label="link to dealer page"
            className={`back-to-dealer ${NAVIGATION_CLASSNAME}`}
          >
            {DealerButtonLabel}
          </TextButton>
          {dealerData?.address && <DealerDetail icon="poi-icon" detail={dealerData?.address} />}
          {dealerData?.phone && <DealerDetail icon="voice-call-1" detail={dealerData?.phone} />}
          {dealerData?.openingHours &&
            dealerData?.openingHours.departments.map((department: Department) => (
              <DepartmentOpeningHours key={department.departmentName} department={department} />
            ))}
          <AdditionalDealerDataActionsBottom isMobileView={isMobileView}>
            <AdditionalDealerDataDisconnectButton
              onClick={() => {
                removeDealer(removeDealerButtonText);
              }}
              aria-label="disconnect from current dealer"
              className={NAVIGATION_CLASSNAME}
            >
              <Layout align="center">
                <Icon name="cancel" size="s" />
              </Layout>
              <Layout align="center">
                <StyledText>{removeDealerButtonText}</StyledText>
              </Layout>
            </AdditionalDealerDataDisconnectButton>
            <AdditionalDealerDataInfoButton>
              <Popover
                aria-label="Popover content for remove dealer"
                placement="top"
                alignment="left"
                open={toggleElement}
                content={
                  <div
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 0,
                      width: '100%',
                      maxWidth: 362,
                    }}
                  >
                    <StyledText tag="p" variant={{ order: '1', style: 'normal', type: 'copy' }}>
                      {removeDealerHelpText}
                    </StyledText>
                  </div>
                }
                id="remove-dealer"
                triggerElement={
                  <IconButton
                    aria-label="triggers popover for remove dealer"
                    icon="info-circle"
                    aria-disabled={!open}
                    onClick={() => {
                      toggle();
                    }}
                    size="sm"
                    variant="tertiary"
                    className={NAVIGATION_CLASSNAME}
                  />
                }
                onClose={() => {}}
                withTip={false}
              />
            </AdditionalDealerDataInfoButton>
          </AdditionalDealerDataActionsBottom>
        </AdditionalDealerDataContent>
      </AdditionalDealerData>
    </ViewportProvider>
  );
};

export default AudiHeaderAdditionalDealerData;
